import type { CityConfig } from "../city-template/types";

export const orangeConfig: CityConfig = {
  city: "Orange",
  state: "California",
  stateAbbr: "CA",
  slug: "orange",
  route: "/cities/orange",
  title: "Emergency Passport Orange, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Orange, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for north Orange County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Orange, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Orange and north Orange County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Orange%20California%20historic%20downtown%20plaza%20at%20golden%20hour%20with%20the%20Old%20Towne%20Orange%20historic%20district%2C%20vintage%20brick%20buildings%20and%20antique%20shops%2C%20Chapman%20University%20campus%20visible%20in%20the%20background%2C%20palm%20trees%20and%20orange%20groves%20in%20the%20distance%2C%20warm%20sunset%20light%20casting%20amber%20tones%2C%20professional%20suburban%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20charming%20and%20historic%20small%20town%20atmosphere&width=1800&height=700&seq=orange-hero-001&orientation=landscape",
  heroImageAlt: "Orange California historic downtown plaza at golden hour with Old Towne Orange district and vintage brick buildings",
  geoPosition: "33.7879;-117.8531",
  geoPlacename: "Orange, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 50–65 minutes from Orange via I-5",
  },
  nearbyCitiesZips: [
    { name: "Orange", zips: "92856, 92861, 92864, 92866, 92867, 92868, 92869" },
    { name: "Anaheim", zips: "92801–92808" },
    { name: "Villa Park", zips: "92861" },
    { name: "Tustin", zips: "92780, 92782" },
    { name: "Santa Ana", zips: "92701–92707" },
    { name: "Fullerton", zips: "92831–92835" },
    { name: "Placentia", zips: "92870, 92871" },
    { name: "Yorba Linda", zips: "92885, 92886, 92887" },
    { name: "Brea", zips: "92821, 92822, 92823" },
    { name: "Garden Grove", zips: "92840–92846" },
    { name: "Irvine", zips: "92602–92604, 92606, 92612, 92614, 92617–92620" },
    { name: "La Habra", zips: "90631, 90632, 90633" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent north Orange County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Anaheim", route: "/cities/anaheim" },
    { name: "Fullerton", route: "/cities/fullerton" },
    { name: "Santa Ana", route: "/cities/santa-ana" },
    { name: "Garden Grove", route: "/cities/garden-grove" },
    { name: "Irvine", route: "/cities/irvine" },
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Long Beach", route: "/cities/long-beach" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Orange remotely via secure courier. We do not have a walk-in office in Orange — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for north Orange County travelers.",
  fpcOfficeAddress: "200 Spectrum Center Dr, Irvine, CA 92618",
  fpcOfficeDistance: "Serving all of north Orange County",
  serviceAreaIntro: "From the historic Old Towne Orange to the Chapman University area, from the Orange Hills to the Orange Park Acres — Fast Passport Center serves the entire Orange community. We regularly help travelers from Anaheim, Fullerton, Villa Park, and Tustin get their passports fast.",
  nearbyCitiesIntro: "We serve all Orange neighborhoods and nearby north Orange County cities including Anaheim, Fullerton, Villa Park, Tustin, Placentia, Yorba Linda, and Brea. If you are in north Orange County, we have you covered.",
  howItWorksIntro: "Most Orange travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With John Wayne Airport (SNA) just minutes away and LAX accessible via I-5, Orange travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Orange and north Orange County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Orange? Here are the most common ones we hear from north Orange County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent north Orange County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Daniel R.",
      location: "Orange, CA",
      rating: 5,
      text: "My flight to London was in 2 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved our business trip completely.",
      service: "Emergency Passport",
    },
    {
      name: "Sophia K.",
      location: "Villa Park, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to the Caribbean. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Robert M.",
      location: "Anaheim, CA",
      rating: 5,
      text: "Lost my passport a week before my trip to Japan. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Emily C.",
      location: "Fullerton, CA",
      rating: 5,
      text: "As a frequent international traveler from north Orange County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "orange-1",
      name: "Orange Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1075 N Tustin St",
      city: "Orange",
      state: "CA",
      zip: "92867",
      phone: "(714) 997-6812",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Old Towne Orange",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "orange-2",
      name: "Anaheim Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "701 N Loara St",
      city: "Anaheim",
      state: "CA",
      zip: "92801",
      phone: "(714) 535-2621",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.5 miles from Orange",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "orange-3",
      name: "Fullerton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "202 E Commonwealth Ave",
      city: "Fullerton",
      state: "CA",
      zip: "92832",
      phone: "(714) 738-9925",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.8 miles from Orange",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for north Orange County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "orange-4",
      name: "Tustin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "17502 17th St",
      city: "Tustin",
      state: "CA",
      zip: "92780",
      phone: "(714) 544-3834",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.2 miles from Orange",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for north OC residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "orange-5",
      name: "Placentia Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1350 E Chapman Ave",
      city: "Placentia",
      state: "CA",
      zip: "92870",
      phone: "(714) 528-0898",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.0 miles from Orange",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern OC residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "orange-6",
      name: "Orange County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "333 W Santa Ana Blvd",
      city: "Santa Ana",
      state: "CA",
      zip: "92701",
      phone: "(714) 834-2500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5.5 miles from Orange",
      website: "https://www.ocassessor.com/",
      websiteLabel: "OC Clerk-Recorder",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "North OC Coverage", agency: "No north Orange County expertise", fpc: "Old Towne Orange, Chapman University, and north OC community travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "North OC photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Orange area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many north OC applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Orange families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Orange travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Orange." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Orange address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Orange before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Orange for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Orange County travelers." },
    { q: "How fast can I renew my passport in Orange?", a: "Fast Passport Center offers fast passport renewal in Orange in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Orange in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Orange?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Orange residents." },
    { q: "Can I expedite a child passport in Orange?", a: "Yes. Fast Passport Center expedites child passports in Orange for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Orange with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Orange?",
      a: "With Fast Passport Center's emergency courier service, Orange travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Orange?",
      a: "Popular options include the Orange Post Office (92867), Anaheim Main Post Office (92801), Fullerton Post Office (92832), and Tustin Post Office (92780). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Orange?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Orange Hills or Orange Park Acres?",
      a: "We serve all Orange neighborhoods including Orange Hills, Orange Park Acres, and the Old Towne district. Our overnight shipping labels work from any UPS location, so your location within Orange doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Orange?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Orange?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Orange, Anaheim, and Fullerton offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Orange?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Orange?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Orange families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Orange", address: "1350 W Chapman Ave, Orange, CA 92868", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Anaheim", address: "101 S Brookhurst St, Anaheim, CA 92804", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Orange", address: "1500 E Village Way, Orange, CA 92866", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Fullerton", address: "2525 W Orangethorpe Ave, Fullerton, CA 92833", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Anaheim", address: "88 E Orangethorpe Ave, Anaheim, CA 92801", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Placentia", address: "225 E Yorba Linda Blvd, Placentia, CA 92870", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Tustin", address: "14011 Red Hill Ave, Tustin, CA 92780", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Yorba Linda", address: "22633 Savi Ranch Pkwy, Yorba Linda, CA 92887", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Fullerton",
      address: "2525 W Orangethorpe Ave, Fullerton, CA 92833",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Santa Ana",
      address: "1330 E First St, Santa Ana, CA 92701",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Anaheim",
      address: "9572 Katella Ave, Anaheim, CA 92804",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Placentia",
      address: "465 N Placentia Ave, Placentia, CA 92870",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for north Orange County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "8 miles from Orange",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "The primary airport for Orange County. International flights to Mexico, Canada, and connections to Europe and Asia.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "38 miles from Orange",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-5.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "25 miles from Orange",
      address: "2900 E Airport Dr, Ontario, CA 91761",
      note: "Growing international airport with flights to Mexico and domestic connections. Easy access via SR-91.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "OC Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Orange County",
      url: "https://www.ocassessor.com/",
    },
    {
      label: "Orange County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Orange County",
      url: "https://www.occourts.org/",
    },
  ],
};